import theme from 'stories/utils/theme'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import NewButton from 'components/elements/NewButton'
import { Body2, Headline3 } from 'stories/elements/Typography/Text'
import Stack from 'components/elements/Stack'
import { useRef } from 'react'
import _useTranslation from 'lib/hooks/_useTranslation'
import AiSummaryAsset from './features/AiSummaryAsset'
import HomeworkScanAsset from './features/HomeworkScanAsset'
import FlashcardsAsset from './features/FlashcardsAsset'
import { AnalyticsEventHomepageFeaturesClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const SectionWrapper = styled.div`
    width: 100%;
    overflow: visible;
    padding: 0px;
`

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    gap: 8px;
    color: ${theme.colors.grayScaleSecondary};
    position: relative;
    justify-content: center;
    align-items: stretch;
    padding: 20px 50px;

    @media (max-width: 860px) {
        all: unset;
        display: flex;
        flex-wrap: nowrap;
        width: 100% !important;
        justify-items: flex-start;
        align-items: flex-start;
        padding: 0 24px;
        gap: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::after {
            content: '';
            flex: 0 0 42px;
            min-width: 42px;
        }

        &::-webkit-scrollbar {
            display: none;
        }
    }
`

const Card = styled.div<{ color: string }>`
    background: ${({ color }) => color};
    border-radius: ${theme.borderRadius.huge};
    padding: 16px;
    max-width: 380px;
    width: 100%;
    min-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    position: relative;
    height: auto;

    p,
    h3 {
        text-align: center;
    }

    h3 {
        font-family: var(--font-inter);
        font-weight: 650;
        font-size: 22px;
        line-height: 28px;
        letter-spacing: -0.2px;
    }
    p {
        font-family: var(--font-inter);
        font-weight: 450;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.3px;
    }

    @media (max-width: 860px) {
        padding: 12px 12px 24px 12px;
        flex: 0 0 320px;
        min-width: 320px;
        max-width: 320px;
        border-radius: ${theme.borderRadius.rounded};
        h3 {
            font-weight: 650 !important;
            font-size: 23px;
            line-height: 28px;
            letter-spacing: -0.2px;
            padding-bottom: 5px;
        }
        p {
            font-size: 14px;
            line-height: 20px;
            font-weight: 450;
            letter-spacing: 0.4px;
        }
    }

    a {
        display: flex;
        justify-content: center;
    }

    .card-content {
        padding: 10px 10px 25px 10px;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 16px;
        margin-bottom: auto;
        width: 100%;
        flex: 1 0 auto;
    }

    .card-image {
        margin-top: auto;
        align-self: center;
        width: 100%;
        display: flex;
        justify-content: center;
        flex: 0 0 auto;
    }
    .button-container {
        padding: 25px 0px 10px 0px;
        z-index: 10;
        p {
            font-family: var(--font-inter);
            font-weight: 650 !important;
            font-size: 17px;
            line-height: 20px;
            letter-spacing: 0.3px;
        }
        @media (max-width: 860px) {
            padding: 25px 0px 0px 0px;
        }
    }
`

interface HomepageFeaturesProps {
    onButtonClick: (referrerScreen: string) => void
}

const HomepageFeatures = ({ onButtonClick }: HomepageFeaturesProps) => {
    const { t } = _useTranslation('homepage')
    const wrapperRef = useRef<HTMLDivElement>(null)

    const data = [
        {
            title: t('homepage/aiSummaryTitle'),
            description: t('homepage/aiSummaryDescription'),
            image: <AiSummaryAsset />,
            buttonText: t('homepage/tryItOut'),
            referrerScreen: 'homepage_ai_summary',
            color: '#004A54',
        },
        {
            title: t('homepage/homeworkScanTitle'),
            description: t('homepage/homeworkScanDescription'),
            image: <HomeworkScanAsset />,
            buttonText: t('homepage/tryItOut'),
            referrerScreen: 'homepage_hw_scan',
            color: '#014B44',
        },
        {
            title: t('homepage/flashcardsTitle'),
            description: t('homepage/flashcardsDescription'),
            image: <FlashcardsAsset />,
            buttonText: t('homepage/tryItOut'),
            referrerScreen: 'homepage_flashcards',
            color: '#1E4B1F',
        },
    ]

    return (
        <SectionContainer
            backgroundColor="linear-gradient(180deg, #051F1B 0%, #0F1E0C 100%)"
            title={t('homepage/featuresTitle')}
            borderRadius="48px"
            mobileBorderRadius="32px"
            fullWidth
            noVerticalPadding
        >
            <SectionWrapper>
                <Wrapper ref={wrapperRef}>
                    {data.map((item, index) => (
                        <Card key={index} color={item.color}>
                            <Stack className="card-content">
                                <Stack gutter={6}>
                                    <Headline3
                                        color={theme.colors.white}
                                        fontSize={22}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={550}
                                    >
                                        {item.title}
                                    </Headline3>
                                    <Body2
                                        color={theme.colors.grayScaleSecondary}
                                        fontWeight={400}
                                        fontSize={14}
                                        fontFamily="var(--font-inter)"
                                    >
                                        {item.description}
                                    </Body2>
                                </Stack>
                            </Stack>
                            <div className="card-image">{item.image}</div>
                            <div className="button-container">
                                <NewButton
                                    onClick={() => {
                                        trackEvent(AnalyticsEventHomepageFeaturesClick, {
                                            referrerScreen: item.referrerScreen,
                                            value: item.title,
                                        })
                                        onButtonClick(item.referrerScreen)
                                    }}
                                    color={theme.colors.white}
                                    borderRadius={theme.borderRadius.webappButton}
                                    height={44}
                                    width="fit-content"
                                >
                                    <Body2 color={theme.colors.black} fontSize={17} fontWeight={650}>
                                        {item.buttonText}
                                    </Body2>
                                </NewButton>
                            </div>
                        </Card>
                    ))}
                </Wrapper>
            </SectionWrapper>
        </SectionContainer>
    )
}

export default HomepageFeatures
